/**
 * The public early-access page: the research-led private-beta landing shown
 * at `/` when the deployment selects it (`PUBLIC_LANDING_VARIANT=coming-soon`).
 *
 * Composition: the original colorful hero, a prepared five-step demo, CPMAI
 * progress, and the closing signup. The hero CTA enters the demo. Signup
 * buttons reuse the existing access dialog and API; all demo steps stay local.
 * Both the hero and additions become inert while either dialog is open.
 * A pending sign-in is still finished by usePendingSignInRedirect.
 *
 * The page shows whichever language the `LanguageProvider` holds and toggles
 * it through the provider, so `<html lang dir>` and the remembered choice
 * behave as on every other page. It hands the provider its own document
 * title while mounted.
 *
 * Two props exist for the review page and the tests: a substitute submit
 * function, and a presentation directive that opens the dialog in a given
 * state. The production route passes neither.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { useLanguage } from '../../i18n/language.js';
import type { Language } from '../../i18n/translations.js';
import { usePendingSignInRedirect } from '../../workspace/pages/pending-sign-in.js';
import { submitEarlyAccessRequest, type SubmitEarlyAccess } from './api.js';
import { PUBLIC_EARLY_ACCESS_COPY } from './copy.js';
import { EarlyAccessDialog } from './EarlyAccessDialog.js';
import { useExampleVideo } from './example-video.js';
import { ExampleVideo } from './ExampleVideo.js';
import { HeroContent } from './HeroContent.js';
import { GuidedDemo } from './GuidedDemo.js';
import { ProjectReadiness } from './ProjectReadiness.js';
import { DemoSignup } from './DemoSignup.js';
import { PublicEarlyAccessHeader } from './PublicEarlyAccessHeader.js';
import { useEarlyAccessForm, type DialogPresentation } from './useEarlyAccessForm.js';
import { VideoLightbox } from './VideoLightbox.js';

export const MAIN_ID = 'main';
const PIXELS = [0, 1, 2, 3, 4, 5] as const;
/** The reference clears the live region, then fills it a beat later, so a repeat is read again. */
const ANNOUNCE_DELAY_MS = 50;

function useAnnouncer(): readonly [string, (text: string) => void] {
  const [message, setMessage] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );
  const announce = useCallback((text: string) => {
    setMessage('');
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      setMessage(text);
    }, ANNOUNCE_DELAY_MS);
  }, []);
  return [message, announce];
}

export function PublicEarlyAccessPage({
  submit = submitEarlyAccessRequest,
  presentation = null,
}: {
  readonly submit?: SubmitEarlyAccess;
  readonly presentation?: DialogPresentation | null;
}): ReactNode {
  const { language, toggle, setDocumentTitle } = useLanguage();
  const copy = PUBLIC_EARLY_ACCESS_COPY[language];
  usePendingSignInRedirect();

  useEffect(() => {
    setDocumentTitle(copy.title);
    return () => {
      setDocumentTitle(null);
    };
  }, [copy.title, setDocumentTitle]);

  const [live, announce] = useAnnouncer();
  const [accessOpen, setAccessOpen] = useState(presentation !== null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const form = useEarlyAccessForm({ submit, language, presentation });
  const video = useExampleVideo({ label: copy.a11yVideo });

  useEffect(() => {
    const root = document.documentElement;
    const previous = root.dataset['inputMethod'];
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Tab' || event.key.startsWith('Arrow'))
        root.dataset['inputMethod'] = 'keyboard';
    };
    const pointer = () => {
      root.dataset['inputMethod'] = 'pointer';
    };
    document.addEventListener('keydown', keyboard);
    document.addEventListener('pointerdown', pointer);
    return () => {
      document.removeEventListener('keydown', keyboard);
      document.removeEventListener('pointerdown', pointer);
      if (previous === undefined) delete root.dataset['inputMethod'];
      else root.dataset['inputMethod'] = previous;
    };
  }, []);

  const pauseHero = () => {
    if (video.playing) video.togglePlay();
  };
  const openDemo = () => {
    pauseHero();
    document
      .querySelector('#demo .mw-shell')
      ?.scrollIntoView({ behavior: 'instant', block: 'start' });
    document
      .querySelector<HTMLElement>('#demo .stage:not([hidden]) h3')
      ?.focus({ preventScroll: true });
    history.replaceState(history.state, '', '#demo');
  };

  const openAccess = useCallback((from: HTMLElement) => {
    opener.current = from;
    setAccessOpen(true);
  }, []);
  const closeAccess = useCallback(() => {
    if (form.phase === 'success') form.reset();
    setAccessOpen(false);
  }, [form]);
  const openLightbox = useCallback((from: HTMLElement) => {
    opener.current = from;
    setLightboxOpen(true);
  }, []);
  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  // Focus returns to whichever button opened the dialog — after the stage
  // has stopped being inert, which is why this is an effect and not part of
  // the close handlers.
  const modal = accessOpen || lightboxOpen;
  useEffect(() => {
    if (modal || opener.current === null) return;
    opener.current.focus({ preventScroll: true });
    opener.current = null;
  }, [modal]);

  // The accepted request is announced once, when the phase arrives — not
  // again when the language changes while the confirmation is showing.
  const previousPhase = useRef(form.phase);
  useEffect(() => {
    if (form.phase === 'success' && previousPhase.current !== 'success') {
      announce(copy.doneLive);
    }
    previousPhase.current = form.phase;
  }, [announce, copy.doneLive, form.phase]);

  const onToggleLanguage = () => {
    const next: Language = language === 'en' ? 'ar' : 'en';
    toggle();
    announce(PUBLIC_EARLY_ACCESS_COPY[next].langSwitched);
  };

  return (
    <>
      <a className="cs-skip-link" href={`#${MAIN_ID}`}>
        {copy.skip}
      </a>
      <div className="cs-viewport">
        <div className="cs-stage" inert={modal}>
          {PIXELS.map((index) => (
            <span className="cs-pixel" aria-hidden="true" key={index} />
          ))}
          <PublicEarlyAccessHeader
            copy={copy}
            language={language}
            mainId={MAIN_ID}
            onToggleLanguage={onToggleLanguage}
            onRequestAccess={openAccess}
          />
          <main className="cs-hero" id={MAIN_ID} tabIndex={-1}>
            <div className="cs-hero-grid">
              <HeroContent copy={copy} onRequestAccess={openDemo} />
              <ExampleVideo copy={copy} video={video} onEnlarge={openLightbox} />
            </div>
          </main>
        </div>

        {lightboxOpen ? (
          <VideoLightbox copy={copy} video={video} onClose={closeLightbox} />
        ) : null}
        {accessOpen ? (
          <EarlyAccessDialog copy={copy} form={form} onClose={closeAccess} />
        ) : null}
        <p className="cs-vh" role="status" aria-live="polite" aria-atomic="true">
          {live}
        </p>
      </div>
      <div id="mouja-additions" inert={modal}>
        <GuidedDemo
          language={language}
          suspended={modal}
          onInteract={pauseHero}
          onRequestAccess={openAccess}
        />
        <ProjectReadiness language={language} />
        <DemoSignup language={language} onRequestAccess={openAccess} />
      </div>
    </>
  );
}
